import { NoDocumentsOnboardingPromptType } from '@/types/schema';

import AbJointTrustPlanIllustration from './assets/ab-joint-trust-plan.svg';
import AbSeparateTrustPlanIllustration from './assets/ab-separate-trust-plan.svg';
import AbcJointTrustPlanIllustration from './assets/abc-joint-trust-plan.svg';
import AbcSeparateTrustPlanIllustration from './assets/abc-separate-trust-plan.svg';
import EqualSharesIllustration from './assets/equal-shares.svg';
import FamilyTrustIllustration from './assets/family-trust.svg';
import SpecificBequestsIllustration from './assets/specific-bequests.svg';
import SurvivingSpouseIllustration from './assets/surviving-spouse.svg';

// Keep these configs free of component imports. The household chat
// transcript reads them, and the onboarding modal imports the chat through
// the route tree, so a component import here creates an import cycle.

export interface PlanTemplate {
  assumptions: string;
  description: string;
  Illustration: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  value: NoDocumentsOnboardingPromptType;
}

const SEPARATE_TRUSTS_AB_PLAN_TEMPLATE: PlanTemplate = {
  value: NoDocumentsOnboardingPromptType.AbSeparateTrusts,
  title: 'A/B (separate revocable trusts)',
  description:
    "Upon first death, the deceased's individual holdings pour into the revocable trust, which then splits into marital and bypass trusts. The bypass trust uses the remaining exemption and passes GST-exempt. The marital trust remains in the survivor’s estate and pass to the children's non-exempt trusts upon the survivor’s death.",
  assumptions:
    'Joint accounts pass to the survivor outright and the deceased’s retirement accounts are distributed to an inherited IRA.',
  Illustration: AbSeparateTrustPlanIllustration,
};

const SEPARATE_TRUSTS_ABC_PLAN_TEMPLATE: PlanTemplate = {
  value: NoDocumentsOnboardingPromptType.AbcSeparateTrusts,
  title: 'A/B/C (separate revocable trusts)',
  description:
    "Upon first death, the deceased's individual holdings pour into the revocable trust, which then splits into marital, bypass, and QTIP trusts. The bypass trust uses the remaining exemption and passes GST-exempt. The marital and QTIP trusts remain in the survivor’s estate and pass to the children's non-exempt trusts upon the survivor’s death.",
  assumptions: SEPARATE_TRUSTS_AB_PLAN_TEMPLATE.assumptions,
  Illustration: AbcSeparateTrustPlanIllustration,
};

const JOINT_TRUST_AB_PLAN_TEMPLATE: PlanTemplate = {
  value: NoDocumentsOnboardingPromptType.AbJointTrust,
  title: 'A/B (joint revocable trust)',
  description:
    "Upon first death, the deceased's individual holdings pour into the joint revocable trust, which then splits into survivor's and bypass trusts. The bypass trust uses the remaining exemption from the deceased’s share and passes GST-exempt. The survivor’s trust holds everything else and passes to the children's non-exempt trusts upon the survivor’s death.",
  assumptions: SEPARATE_TRUSTS_AB_PLAN_TEMPLATE.assumptions,
  Illustration: AbJointTrustPlanIllustration,
};

const JOINT_TRUST_ABC_PLAN_TEMPLATE: PlanTemplate = {
  value: NoDocumentsOnboardingPromptType.AbcJointTrust,
  title: 'A/B/C (joint revocable trust)',
  description:
    "Upon first death, the deceased's individual holdings pour into the revocable trust, which then splits into survivor's, bypass, and marital trusts. The bypass trust uses the remaining exemption from the deceased’s share and passes GST-exempt. The marital trust holds the remainder of the deceased’s share while the survivor’s trust holds the survivor's share, and both pass to the children's non-exempt trusts upon the survivor’s death.",
  assumptions: SEPARATE_TRUSTS_AB_PLAN_TEMPLATE.assumptions,
  Illustration: AbcJointTrustPlanIllustration,
};

export const SEPARATE_TRUSTS_PLAN_TEMPLATES: PlanTemplate[] = [
  SEPARATE_TRUSTS_ABC_PLAN_TEMPLATE,
  SEPARATE_TRUSTS_AB_PLAN_TEMPLATE,
];

export const JOINT_TRUST_PLAN_TEMPLATES: PlanTemplate[] = [
  JOINT_TRUST_ABC_PLAN_TEMPLATE,
  JOINT_TRUST_AB_PLAN_TEMPLATE,
];

export const SURVIVING_SPOUSE_TEMPLATE: PlanTemplate = {
  value: NoDocumentsOnboardingPromptType.SurvivingSpouse,
  title: '100% to surviving spouse',
  description:
    "Upon first death, all assets pass outright to the surviving spouse, then outright to children in separate shares upon second death. The first spouse's exemption carries over only through a portability election.",
  assumptions: SEPARATE_TRUSTS_AB_PLAN_TEMPLATE.assumptions,
  Illustration: SurvivingSpouseIllustration,
};

const FAMILY_TRUST_TEMPLATE: PlanTemplate = {
  value: NoDocumentsOnboardingPromptType.FamilyTrust,
  title:
    'Pour over will with revocable trust, distributing to Family trust and descendants',
  description:
    'Upon death, individual holdings pour into the revocable trust, which then distributes the deceased’s remaining exemption plus any DSUE to a family trust. The remainder distributes equally to descendants, outright or in trust.',
  assumptions:
    'The deceased’s retirement accounts are distributed to named beneficiaries.',
  Illustration: FamilyTrustIllustration,
};

const EQUAL_SHARES_TEMPLATE: PlanTemplate = {
  value: NoDocumentsOnboardingPromptType.EqualShares,
  title:
    'Pour over will with revocable trust, distributing to descendants in equal shares',
  description:
    'Upon death, individual holdings pour into the revocable trust, which then distributes equally to descendants, outright or in trust.',
  assumptions: FAMILY_TRUST_TEMPLATE.assumptions,
  Illustration: EqualSharesIllustration,
};

const SPECIFIC_BEQUESTS_TEMPLATE: PlanTemplate = {
  value: NoDocumentsOnboardingPromptType.SpecificBequests,
  title: 'Will with specific bequests',
  description:
    'Specific gifts are made as specified by the will, and the remainder is distributed to descendants and/or charity, either directly or separate trusts.',
  assumptions:
    'Individual accounts pass to beneficiaries as specified in the will, and the deceased retirement accounts pass to named beneficiaries',
  Illustration: SpecificBequestsIllustration,
};

export const SINGLE_GRANTOR_TEMPLATES: PlanTemplate[] = [
  SPECIFIC_BEQUESTS_TEMPLATE,
  EQUAL_SHARES_TEMPLATE,
  FAMILY_TRUST_TEMPLATE,
];

/** Every no-documents plan template, keyed by its prompt type. */
export const NO_DOCUMENTS_PLAN_TEMPLATES: Record<
  NoDocumentsOnboardingPromptType,
  PlanTemplate
> = {
  [NoDocumentsOnboardingPromptType.AbJointTrust]: JOINT_TRUST_AB_PLAN_TEMPLATE,
  [NoDocumentsOnboardingPromptType.AbSeparateTrusts]:
    SEPARATE_TRUSTS_AB_PLAN_TEMPLATE,
  [NoDocumentsOnboardingPromptType.AbcJointTrust]:
    JOINT_TRUST_ABC_PLAN_TEMPLATE,
  [NoDocumentsOnboardingPromptType.AbcSeparateTrusts]:
    SEPARATE_TRUSTS_ABC_PLAN_TEMPLATE,
  [NoDocumentsOnboardingPromptType.EqualShares]: EQUAL_SHARES_TEMPLATE,
  [NoDocumentsOnboardingPromptType.FamilyTrust]: FAMILY_TRUST_TEMPLATE,
  [NoDocumentsOnboardingPromptType.SpecificBequests]:
    SPECIFIC_BEQUESTS_TEMPLATE,
  [NoDocumentsOnboardingPromptType.SurvivingSpouse]: SURVIVING_SPOUSE_TEMPLATE,
};
